import React, { useState, useEffect, useRef } from "react";
import { Copy, Check, AlertTriangle } from "lucide-react";
import { modelBrand } from "../../lib/modelBrand";
import SpeakButton from "./SpeakButton";
import PinButton from "./PinButton";
import MarkdownAnswer from "./MarkdownAnswer";
import { useLang } from "../../contexts/LanguageContext";

/**
 * ResultPanel — chat transcript for a single model across a session.
 *
 * Props:
 *  - model: { id, label, provider }
 *  - turns: array of { query, attachments, result: { ok, content, error, latency_ms, streaming } }
 *           Ordered chronologically. The last turn is the "live" one during streaming.
 *  - loading, pinned, onTogglePin (pin / copy apply to the LATEST completed turn only)
 */
export default function ResultPanel({ model, turns, loading, pinned, onTogglePin }) {
  const [copied, setCopied] = useState(false);
  const { t } = useLang();
  const brand = modelBrand(model.id);
  const { Icon } = brand;
  const scrollerRef = useRef(null);

  const safeTurns = Array.isArray(turns) ? turns : [];
  const lastTurn = safeTurns[safeTurns.length - 1] || null;
  const lastResult = lastTurn?.result || null;
  const streaming = !!(lastResult && lastResult.streaming);

  // Auto-scroll to the newest message inside this panel when content arrives.
  useEffect(() => {
    if (!scrollerRef.current) return;
    scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
  }, [safeTurns.length, lastResult?.content, lastResult?.streaming]);

  const doCopy = async () => {
    const text = lastResult?.content;
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
    } catch {
      /* ignore */
    }
  };

  return (
    <div
      className={`${brand.accentClass} flex flex-col bg-[#0A0A0A] ${
        pinned ? "ring-1 ring-[#F5C542]/50" : ""
      }`}
      data-testid={`result-panel-${model.id}`}
      data-pinned={pinned ? "true" : "false"}
    >
      {/* Header */}
      <div className="sticky top-0 z-10 px-4 py-3 border-b border-white/10 bg-black/90 backdrop-blur-md flex items-center justify-between">
        <div className="flex items-center gap-3 min-w-0">
          <span className="w-2 h-2 rounded-full accent-dot flex-shrink-0" />
          <Icon className="w-4 h-4 flex-shrink-0 text-white/80" />
          <div className="min-w-0">
            <div className="label-eyebrow truncate">{model.provider}</div>
            <div className="text-[13px] font-medium text-white/90 truncate font-display">
              {model.label}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {lastResult && !streaming && (
            <span className="label-eyebrow" data-testid={`latency-${model.id}`}>
              {lastResult.latency_ms}ms
            </span>
          )}
          {lastResult?.ok && !streaming && lastResult?.content && (
            <SpeakButton text={lastResult.content} testId={`speak-${model.id}`} />
          )}
          {lastResult?.ok && !streaming && onTogglePin && (
            <PinButton
              modelId={model.id}
              pinned={!!pinned}
              onToggle={() => onTogglePin(model.id)}
              disabled={loading}
            />
          )}
          <button
            onClick={doCopy}
            disabled={!lastResult?.content}
            className="btn-ghost !h-8 !px-3 focus-ring disabled:opacity-30"
            aria-label={t("panel.copyAria")}
            data-testid={`copy-${model.id}`}
          >
            {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
            <span className="text-[11px]">{copied ? t("panel.copied") : t("panel.copy")}</span>
          </button>
        </div>
      </div>

      <div className="sticky top-[57px] z-10 h-[1px] accent-underline" />

      {/* Chat transcript */}
      <div
        ref={scrollerRef}
        className="max-h-[30vh] overflow-y-auto thin-scroll px-4 py-4 flex flex-col gap-4"
        data-testid={`panel-transcript-${model.id}`}
      >
        {safeTurns.length === 0 && (
          <div className="font-mono-p text-white/30 text-[12px] uppercase tracking-[0.2em]">
            {t("panel.awaiting")}
          </div>
        )}

        {safeTurns.map((turn, i) => {
          const isLast = i === safeTurns.length - 1;
          const r = turn.result || {};
          const showTracing = isLast && r.streaming && !r.content && !r.error;
          const showError = !r.streaming && !r.ok && r.error;
          return (
            <div key={turn.search_id || `t-${i}`} className="flex flex-col gap-2 fade-in">
              {/* User bubble — right side */}
              <div className="flex justify-end">
                <div
                  className="max-w-[85%] rounded-2xl rounded-br-md bg-white/10 border border-white/10 text-white/95 px-3.5 py-2 text-[13.5px] leading-relaxed break-words"
                  data-testid={`turn-user-${model.id}-${i}`}
                >
                  {turn.query}
                </div>
              </div>

              {/* AI bubble — left side */}
              <div className="flex justify-start">
                <div
                  className="max-w-[92%] rounded-2xl rounded-bl-md bg-white/[0.03] border border-white/10 text-white/85 px-3.5 py-2.5 text-[14px] leading-relaxed panel-content"
                  data-testid={`turn-ai-${model.id}-${i}`}
                >
                  {showTracing && (
                    <div className="font-mono-p text-white/50 text-[13px]">
                      <span className="cursor-blink">{t("panel.tracing")}</span>
                    </div>
                  )}
                  {r.content && !r.error && (
                    <>
                      <MarkdownAnswer text={r.content} />
                      {isLast && r.streaming && (
                        <span className="cursor-blink inline-block" aria-hidden="true" />
                      )}
                    </>
                  )}
                  {showError && (
                    <div className="flex items-start gap-2 text-[13px] text-[#ff9c85]">
                      <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                      <div>
                        <div className="uppercase text-[10px] tracking-[0.25em] mb-1 text-white/50">
                          {t("panel.error")}
                        </div>
                        <div className="font-mono-p break-words">
                          {r.error || t("panel.unknownError")}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
